import React, { useEffect, useRef, useState } from "react"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import LinearProgress from "@mui/material/LinearProgress"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import Box from "@mui/material/Box"
import FormControlLabel from "@mui/material/FormControlLabel"
import Switch from "@mui/material/Switch"
import { useDeployEvents } from "./DeployEventContext"
import { loadSshSettings } from "../terminal/TerminalContext"

const FALLBACK_TIMEOUT_S = 30  // used only until the server's timeout_s arrives

const PROJECT_LABELS = {
  "ai-assistant": "AI Assistant",
  "huts-heroes-dev": "Hut & Heroes (dev)",
  "huts-heroes": "Hut & Heroes (production ⚠)",
}

function devChipColors(response) {
  if (response === true)  return { bg: "rgba(34,197,94,0.15)",  color: "var(--app-success)", border: "rgba(34,197,94,0.35)" }
  if (response === false) return { bg: "rgba(239,68,68,0.15)",  color: "var(--app-error)", border: "rgba(239,68,68,0.35)" }
  return                         { bg: "rgba(234,179,8,0.12)",  color: "var(--app-warning)", border: "rgba(234,179,8,0.3)" }
}

export default function DeployConfirmDialog() {
  const { deployState, confirm, aiAlwaysAllow, setAiAlwaysAllow, respondProposal } = useDeployEvents()
  const myUsername = loadSshSettings().username || ""
  const open = deployState.status === "awaiting_confirm"
  const [elapsed, setElapsed] = useState(0)
  const [answered, setAnswered] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [cancelNote, setCancelNote] = useState("")
  const [proposalBusy, setProposalBusy] = useState(false)
  const [proposalError, setProposalError] = useState("")
  const timerRef = useRef(null)

  const total = deployState.timeout_s || FALLBACK_TIMEOUT_S

  useEffect(() => {
    if (open) {
      // Resume mid-countdown after a reload: the server state carries remaining_s.
      setElapsed(Math.max(0, total - (deployState.remaining_s ?? total)))
      setAnswered(false)
      setCancelling(false)
      setCancelNote("")
      timerRef.current = setInterval(() => {
        setElapsed((s) => s + 1)
      }, 1000)
    } else {
      clearInterval(timerRef.current)
      setElapsed(0)
      setAnswered(false)
      setCancelling(false)
      setCancelNote("")
    }
    return () => clearInterval(timerRef.current)
  }, [open, deployState.id])

  const remaining = Math.max(0, total - elapsed)
  const progress = Math.min(100, (elapsed / total) * 100)
  const waitingForPreflight = open && deployState.expected.length > 0 && deployState.expected.every((name) => deployState.responses?.[name] === true)
  const timedOut = open && remaining <= 0 && !waitingForPreflight

  const handleConfirm = () => {
    setAnswered(true)
    confirm(deployState.id, true)
  }

  const handleSendCancel = () => {
    setAnswered(true)
    confirm(deployState.id, false, cancelNote.trim().slice(0, 500))
  }

  const isRequester = deployState.requested_by === myUsername
  const proposal = deployState.proposal
  const proposalAgent = proposal?.agent === "claude" ? "Claude" : "Codex"

  useEffect(() => {
    setProposalBusy(false)
    setProposalError("")
  }, [proposal?.id])

  const resolveProposal = async (accepted) => {
    setProposalBusy(true)
    setProposalError("")
    try {
      const result = await respondProposal(proposal, accepted)
      if (!result.ok) {
        setProposalError(result.message || result.error || "Vorschlag konnte nicht verarbeitet werden.")
        setProposalBusy(false)
      }
    } catch (error) {
      setProposalError(error.message || "Vorschlag konnte nicht verarbeitet werden.")
      setProposalBusy(false)
    }
  }

  return (
    <>
    <Dialog open={!!proposal} PaperProps={{ sx: { bgcolor: "var(--app-paper)", border: "1px solid rgba(34,211,238,0.35)", borderRadius: 2, minWidth: 380, maxWidth: 560 } }}>
      <DialogTitle sx={{ color: "var(--feature-stats)", fontFamily: "var(--app-font-mono)", fontSize: "0.9rem", pb: 1 }}>
        {proposalAgent} möchte deployen
      </DialogTitle>
      <DialogContent>
        <Chip size="small" label={PROJECT_LABELS[proposal?.project] ?? proposal?.project} sx={{ mb: 1.5, color: proposal?.project === "huts-heroes" ? "var(--app-warning)" : "var(--feature-stats)" }} />
        <Typography sx={{ color: "#f8fafc", fontWeight: 700, fontSize: "0.9rem" }}>{proposal?.title}</Typography>
        <Typography sx={{ mt: 0.7, color: "var(--app-text-dim)", fontSize: "0.76rem", lineHeight: 1.6 }}>{proposal?.summary}</Typography>
        <FormControlLabel
          sx={{ mt: 1.5, color: "var(--app-text)", "& .MuiFormControlLabel-label": { fontSize: "0.7rem" } }}
          control={<Switch size="small" checked={!!aiAlwaysAllow?.[proposal?.agent]} onChange={(event) => setAiAlwaysAllow(proposal.agent, event.target.checked)} />}
          label={`${proposalAgent}-Vorschläge künftig immer freigeben`}
        />
        <Typography sx={{ color: "var(--app-text-dim)", fontSize: "0.6rem" }}>Preflight, aktive Locks und Freigaben anderer Online-Devs bleiben aktiv.</Typography>
        {!!proposalError && <Typography sx={{ mt: 1.2, color: "var(--app-error)", fontSize: "0.68rem" }}>{proposalError}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button disabled={proposalBusy} onClick={() => resolveProposal(false)} sx={{ color: "var(--app-error)" }}>Ablehnen</Button>
        <Button disabled={proposalBusy} variant="outlined" onClick={() => resolveProposal(true)} sx={{ color: "var(--app-success)", borderColor: "rgba(34,197,94,.45)" }}>Prüfen & freigeben</Button>
      </DialogActions>
    </Dialog>
    <Dialog open={open} PaperProps={{ sx: { bgcolor: "var(--app-paper)", border: "1px solid rgba(168,85,247,0.3)", borderRadius: 2, minWidth: 360 } }}>
      <DialogTitle sx={{ color: "var(--feature-vault)", fontFamily: "var(--app-font-mono)", fontSize: "0.9rem", pb: 1 }}>
        Deploy angefordert
        {deployState.project && (
          <Typography component="span" sx={{ ml: 1, fontSize: "0.65rem", color: deployState.project === "huts-heroes" ? "var(--app-warning)" : "var(--feature-stats)", fontFamily: "var(--app-font-mono)" }}>
            [{PROJECT_LABELS[deployState.project] ?? deployState.project}]
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        <Typography sx={{ color: "var(--app-text-dim)", fontSize: "0.8rem", fontFamily: "var(--app-font-mono)", mb: 1.5 }}>
          {isRequester
            ? `Du (${deployState.requested_by}) hast einen Deploy angefordert.`
            : `${deployState.requested_by || "Jemand"} hat einen Deploy angefordert.`
          }{" "}Alle online Devs müssen bestätigen.
        </Typography>

        {(deployState.title || deployState.summary) && (
          <Box sx={{ mb: 1.5, p: 1.2, borderRadius: 1.5, bgcolor: "rgba(var(--app-fg-rgb),.035)", border: "1px solid rgba(var(--app-fg-rgb),.08)" }}>
            <Typography sx={{ color: "#f8fafc", fontSize: "0.76rem", fontWeight: 700 }}>{deployState.title}</Typography>
            <Typography sx={{ mt: 0.35, color: "var(--app-text-dim)", fontSize: "0.66rem", lineHeight: 1.5 }}>{deployState.summary}</Typography>
            {!!deployState.proposed_by && <Chip size="small" label={`Vorgeschlagen von ${deployState.proposed_by === "claude" ? "Claude" : "Codex"}`} sx={{ mt: 0.8, height: 20, fontSize: "0.58rem" }} />}
          </Box>
        )}

        {deployState.expected.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
            {deployState.expected.map((name) => {
              const response = deployState.responses?.[name]
              const { bg, color, border } = devChipColors(response)
              const isMe = name === myUsername
              return (
                <Chip
                  key={name}
                  label={isMe ? `${name} (ich)` : name}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.65rem",
                    fontFamily: "var(--app-font-mono)",
                    bgcolor: bg,
                    color,
                    border: `1px solid ${border}`,
                    fontWeight: isMe ? 700 : 400,
                  }}
                />
              )
            })}
          </Box>
        )}

        <Box sx={{ mb: 0.5 }}>
          <Typography sx={{ color: remaining <= 5 ? "var(--app-error)" : "var(--app-warning)", fontSize: "0.7rem", fontFamily: "var(--app-font-mono)", mb: 0.5 }}>
            {waitingForPreflight ? "Alle Freigaben da — Preflight läuft …" : timedOut ? "Timeout — Deploy wird abgebrochen …" : `Timeout in ${remaining}s`}
          </Typography>
          <LinearProgress
            variant={waitingForPreflight ? "indeterminate" : "determinate"}
            value={progress}
            sx={{ height: 3, borderRadius: 2, bgcolor: "rgba(234,179,8,0.15)", "& .MuiLinearProgress-bar": { bgcolor: remaining <= 5 ? "var(--app-error)" : "var(--app-warning)" } }}
          />
        </Box>

        {cancelling && (
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            autoFocus
            label="Notiz zum Abbruch (optional)"
            value={cancelNote}
            onChange={(e) => setCancelNote(e.target.value)}
            inputProps={{ maxLength: 500 }}
            sx={{
              mt: 1.5,
              "& .MuiOutlinedInput-root": {
                fontFamily: "var(--app-font-mono)", fontSize: "0.72rem", color: "var(--app-text)",
                "& fieldset": { borderColor: "rgba(239,68,68,0.3)" },
                "&:hover fieldset": { borderColor: "rgba(239,68,68,0.5)" },
                "&.Mui-focused fieldset": { borderColor: "var(--app-error)" },
              },
              "& .MuiInputLabel-root": { fontFamily: "var(--app-font-mono)", fontSize: "0.7rem", color: "var(--app-text-dim)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "var(--app-error)" },
            }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        {cancelling ? (
          <>
            <Button
              size="small"
              variant="outlined"
              disabled={answered || timedOut}
              onClick={() => setCancelling(false)}
              sx={{ fontSize: "0.7rem", fontFamily: "var(--app-font-mono)", color: "var(--app-text-dim)", borderColor: "rgba(148,163,184,0.4)", textTransform: "none", "&:hover": { borderColor: "var(--app-text-dim)", bgcolor: "rgba(148,163,184,0.08)" } }}
            >
              Zurück
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={answered || timedOut}
              onClick={handleSendCancel}
              sx={{ fontSize: "0.7rem", fontFamily: "var(--app-font-mono)", color: "var(--app-error)", borderColor: "rgba(239,68,68,0.4)", textTransform: "none", "&:hover": { borderColor: "var(--app-error)", bgcolor: "rgba(239,68,68,0.08)" } }}
            >
              Abbruch senden
            </Button>
          </>
        ) : (
          <>
            <Button
              size="small"
              variant="outlined"
              disabled={answered || timedOut}
              onClick={() => setCancelling(true)}
              sx={{ fontSize: "0.7rem", fontFamily: "var(--app-font-mono)", color: "var(--app-error)", borderColor: "rgba(239,68,68,0.4)", textTransform: "none", "&:hover": { borderColor: "var(--app-error)", bgcolor: "rgba(239,68,68,0.08)" } }}
            >
              Abbrechen
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={answered || timedOut}
              onClick={handleConfirm}
              sx={{ fontSize: "0.7rem", fontFamily: "var(--app-font-mono)", color: "var(--app-success)", borderColor: "rgba(34,197,94,0.4)", textTransform: "none", "&:hover": { borderColor: "var(--app-success)", bgcolor: "rgba(34,197,94,0.08)" } }}
            >
              Bestätigen
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
    </>
  )
}
